import React, { forwardRef, useEffect, useRef, useState } from "react";

import { ArrowDownRegular } from "@fluentui/react-icons";

import { useMessageListStyles } from "./styles";
import MessageRenderer from "./MessageRenderer";
import { MessageListProps, MessageListRef } from "./types";

const MessageList = React.memo(forwardRef<MessageListRef, MessageListProps>(
  (
    {
      isOpen,
      messages,
	  onDismiss,
      onRetry,
      onTypingComplete,
      onTypingProgress,
      threadId,
    },
    _ref
  ) => {
    const styles = useMessageListStyles();
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [isUserScrolling, setIsUserScrolling] = useState(false);
    const lastScrollTopRef = useRef<number>(0);

    const scrollToBottom = React.useCallback(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        setShowScrollButton(false);
      }
    }, []);

    const handleScroll = React.useCallback(() => {
      if (!containerRef.current) return;

      const { clientHeight, scrollHeight, scrollTop } = containerRef.current;
      const threshold = 10;
      const isAtBottom = scrollHeight - scrollTop - clientHeight <= threshold;
      const isScrollingUp = scrollTop < lastScrollTopRef.current;

      lastScrollTopRef.current = scrollTop;

      if (isScrollingUp && !isAtBottom) {
        setIsUserScrolling(true);
      }

      setShowScrollButton(!isAtBottom);
    }, []);

    const handleTypingProgress = React.useCallback(() => {
      if (!isUserScrolling && messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "auto" });
      }
      if (onTypingProgress) {
        onTypingProgress();
      }
    }, [onTypingProgress, isUserScrolling]);

    useEffect(() => {
      if (messages.length > 0) {
        // Re-enable auto-scroll when a new message arrives
        setIsUserScrolling(false);

        const timer = setTimeout(() => {
          scrollToBottom();
        }, 100);

        return () => clearTimeout(timer);
      }
    }, [messages.length, threadId, scrollToBottom]);

    React.useImperativeHandle(_ref, () => ({
      scrollToBottom,
    }));

    if (messages.length === 0) {
      return <div />;
    }

    return (
      <div className={styles.messageContainer}>
        <div
          className={styles.messagesWrapper}
          onScroll={handleScroll}
          ref={containerRef}
        >
          {messages.map((message) => (
            <div
              className={isOpen ? styles.fadeIn : styles.fadeOut}
              key={message.id}
            >
              <MessageRenderer
                message={message}
                onDismiss={onDismiss}
                onRetry={onRetry}
                onTypingComplete={onTypingComplete}
                onTypingProgress={handleTypingProgress}
              />
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {showScrollButton && (
          <button
            aria-label="Scroll to bottom"
            className={styles.scrollButton}
            onClick={scrollToBottom}
			type="button"
          >
            <ArrowDownRegular />
          </button>
        )}
      </div>
    );
  }
));

MessageList.displayName = "MessageList";

export default MessageList;
